import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Plus, KeyRound, Trash2, Edit2, UserCog, UserX, UserCheck, Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import SearchableSelect from '@/components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [formData, setFormData] = useState({
    email: '', password: '', name: '', role: 'recruiter', team_id: '', employee_number: '', designation: '', department: '', date_of_birth: '', date_of_joining: '', marriage_anniversary: '', date_of_exit: '', is_active: true
  });
  const [editData, setEditData] = useState({
    name: '', role: '', team_id: '', employee_number: '', designation: '', department: '', date_of_birth: '', date_of_joining: '', marriage_anniversary: '', date_of_exit: '', is_active: true
  });

  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: getHeaders() });
      setUsers(res.data);
    } catch (e) { console.error('Failed to fetch users:', e); toast.error('Failed to fetch users'); }
  }, []);

  const fetchTeams = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/teams`, { headers: getHeaders() });
      setTeams(res.data);
    } catch (e) { console.error('Failed to fetch teams:', e); }
  }, []);

  useEffect(() => { fetchUsers(); fetchTeams(); }, [fetchUsers, fetchTeams]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, formData, { headers: getHeaders() });
      toast.success('User created successfully');
      setAddOpen(false);
      setFormData({ email: '', password: '', name: '', role: 'recruiter', team_id: '', employee_number: '', designation: '', department: '', date_of_birth: '', date_of_joining: '', marriage_anniversary: '', date_of_exit: '', is_active: true });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      if (editData.name) payload.name = editData.name;
      if (editData.role) payload.role = editData.role;
      if (editData.team_id !== undefined) payload.team_id = editData.team_id;
      if (editData.employee_number !== undefined) payload.employee_number = editData.employee_number;
      if (editData.designation !== undefined) payload.designation = editData.designation;
      if (editData.department !== undefined) payload.department = editData.department;
      if (editData.date_of_birth !== undefined) payload.date_of_birth = editData.date_of_birth;
      if (editData.date_of_joining !== undefined) payload.date_of_joining = editData.date_of_joining;
      if (editData.marriage_anniversary !== undefined) payload.marriage_anniversary = editData.marriage_anniversary;
      if (editData.date_of_exit !== undefined) payload.date_of_exit = editData.date_of_exit;
      if (editData.is_active !== undefined) payload.is_active = editData.is_active;
      await axios.put(`${API}/admin/users/${selectedUser.id}`, payload, { headers: getHeaders() });
      toast.success('User updated successfully');
      setEditOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/reset-password`, { new_password: newPassword }, { headers: getHeaders() });
      toast.success('Password reset successfully');
      setResetOpen(false);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}`, { headers: getHeaders() });
      toast.success('User deleted successfully');
      setDeleteOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const roleLabel = (role) => {
    const map = { admin: 'Admin', admin_support: 'Admin Support', team_leader: 'Team Leader', recruiter: 'Recruiter', intern: 'Intern', it: 'IT', finance: 'Finance', marketing: 'Marketing' };
    return map[role] || role?.replace('_', ' ');
  };

  const roleBadge = (role) => {
    const cls = {
      admin: 'bg-blue-100 text-blue-800',
      team_leader: 'bg-purple-100 text-purple-800',
      recruiter: 'bg-teal-100 text-teal-800',
      intern: 'bg-amber-100 text-amber-800',
      it: 'bg-indigo-100 text-indigo-800',
      finance: 'bg-emerald-100 text-emerald-800',
      marketing: 'bg-rose-100 text-rose-800',
    };
    return cls[role] || 'bg-slate-100 text-slate-800';
  };

  const filteredUsers = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    let base = users;
    if (statusFilter === 'active') base = users.filter(u => u.is_active !== false);
    else if (statusFilter === 'inactive') base = users.filter(u => u.is_active === false);
    if (!q) return base;
    return base.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q) ||
      (u.team_name || '').toLowerCase().includes(q) ||
      (u.employee_number || '').toLowerCase().includes(q) ||
      (u.designation || '').toLowerCase().includes(q) ||
      (u.department || '').toLowerCase().includes(q)
    );
  }, [users, statusFilter, searchText]);

  const activeCount = users.filter(u => u.is_active !== false).length;
  const inactiveCount = users.filter(u => u.is_active === false).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="user-mgmt-title" style={{ fontFamily: 'Manrope, sans-serif' }}>User Management</h1>
          <p className="text-slate-600">Add, edit, and manage users across your organization</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
              <Plus size={16} className="mr-2" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-blue-900">Add New User</DialogTitle>
              <DialogDescription>Create a new recruiter or team leader account.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4 mt-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Full Name</Label>
                <Input data-testid="user-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="mt-2 rounded border-blue-200" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Email</Label>
                <Input data-testid="user-email-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="mt-2 rounded border-blue-200" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Password</Label>
                <Input data-testid="user-password-input" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required className="mt-2 rounded border-blue-200" placeholder="Minimum 6 characters" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Role</Label>
                  <select data-testid="user-role-select" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-blue-600">
                    <option value="recruiter">Recruiter</option>
                    <option value="team_leader">Team Leader</option>
                    <option value="admin_support">Admin Support</option>
                    <option value="admin">Admin</option>
                    <option value="intern">Intern</option>
                    <option value="it">IT</option>
                    <option value="finance">Finance</option>
                    <option value="marketing">Marketing</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Team</Label>
                  <select data-testid="user-team-select" value={formData.team_id} onChange={(e) => setFormData({ ...formData, team_id: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-blue-600 sr-only">
                    <option value="">No Team</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <div className="mt-2">
                    <SearchableSelect value={formData.team_id} onChange={(id) => setFormData({ ...formData, team_id: id })}
                      items={teams} emptyLabel="No Team" placeholder="Search team…" testId="user-team"
                      allowClear noneLabel="— No Team —" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Employee Number</Label>
                  <Input data-testid="user-employee-number" value={formData.employee_number} onChange={(e) => setFormData({ ...formData, employee_number: e.target.value })} className="mt-2 rounded border-blue-200" placeholder="e.g., EMP001" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Designation</Label>
                  <Input data-testid="user-designation" value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} className="mt-2 rounded border-blue-200" placeholder="e.g., Senior Recruiter" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Department</Label>
                  <Input data-testid="user-department" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} className="mt-2 rounded border-blue-200" placeholder="e.g., Recruitment, HR, IT" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Date of Joining</Label>
                  <Input data-testid="user-doj" type="date" value={formData.date_of_joining} onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })} className="mt-2 rounded border-blue-200" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Date of Birth</Label>
                  <Input data-testid="user-dob" type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} className="mt-2 rounded border-blue-200" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Marriage Anniversary</Label>
                  <Input data-testid="user-marriage-anniv" type="date" value={formData.marriage_anniversary} onChange={(e) => setFormData({ ...formData, marriage_anniversary: e.target.value })} className="mt-2 rounded border-blue-200" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Date of Exit</Label>
                  <Input data-testid="user-date-of-exit" type="date" value={formData.date_of_exit} onChange={(e) => setFormData({ ...formData, date_of_exit: e.target.value })} className="mt-2 rounded border-blue-200" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Status</Label>
                  <div className="mt-2 flex items-center gap-3 h-10">
                    <button type="button" data-testid="user-active-toggle"
                      onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <span className={`text-sm font-semibold ${formData.is_active ? 'text-emerald-700' : 'text-red-600'}`}>
                      {formData.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              <Button data-testid="submit-add-user" type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Create User</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Edit User</DialogTitle>
            <DialogDescription>Update user details, role, or team assignment.</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <form onSubmit={handleEdit} className="space-y-4 mt-4">
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm font-semibold text-blue-900">{selectedUser.email}</p>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Name</Label>
                <Input data-testid="edit-user-name" value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} className="mt-2 rounded border-blue-200" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Role</Label>
                  <select data-testid="edit-user-role" value={editData.role} onChange={(e) => setEditData({ ...editData, role: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-blue-600">
                    <option value="recruiter">Recruiter</option>
                    <option value="team_leader">Team Leader</option>
                    <option value="admin_support">Admin Support</option>
                    <option value="admin">Admin</option>
                    <option value="intern">Intern</option>
                    <option value="it">IT</option>
                    <option value="finance">Finance</option>
                    <option value="marketing">Marketing</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Team</Label>
                  <select data-testid="edit-user-team" value={editData.team_id} onChange={(e) => setEditData({ ...editData, team_id: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-blue-600 sr-only">
                    <option value="">No Team</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <div className="mt-2">
                    <SearchableSelect value={editData.team_id} onChange={(id) => setEditData({ ...editData, team_id: id })}
                      items={teams} emptyLabel="No Team" placeholder="Search team…" testId="edit-user-team-search"
                      allowClear noneLabel="— No Team —" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Employee Number</Label>
                  <Input data-testid="edit-user-employee-number" value={editData.employee_number} onChange={(e) => setEditData({ ...editData, employee_number: e.target.value })} className="mt-2 rounded border-blue-200" placeholder="e.g., EMP001" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Designation</Label>
                  <Input data-testid="edit-user-designation" value={editData.designation} onChange={(e) => setEditData({ ...editData, designation: e.target.value })} className="mt-2 rounded border-blue-200" placeholder="e.g., Senior Recruiter" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Department</Label>
                  <Input data-testid="edit-user-department" value={editData.department} onChange={(e) => setEditData({ ...editData, department: e.target.value })} className="mt-2 rounded border-blue-200" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Date of Joining</Label>
                  <Input data-testid="edit-user-doj" type="date" value={editData.date_of_joining} onChange={(e) => setEditData({ ...editData, date_of_joining: e.target.value })} className="mt-2 rounded border-blue-200" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Date of Birth</Label>
                  <Input data-testid="edit-user-dob" type="date" value={editData.date_of_birth} onChange={(e) => setEditData({ ...editData, date_of_birth: e.target.value })} className="mt-2 rounded border-blue-200" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Marriage Anniversary</Label>
                  <Input data-testid="edit-user-marriage-anniv" type="date" value={editData.marriage_anniversary} onChange={(e) => setEditData({ ...editData, marriage_anniversary: e.target.value })} className="mt-2 rounded border-blue-200" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Date of Exit</Label>
                  <Input data-testid="edit-user-date-of-exit" type="date" value={editData.date_of_exit} onChange={(e) => setEditData({ ...editData, date_of_exit: e.target.value })} className="mt-2 rounded border-blue-200" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Status</Label>
                  <div className="mt-2 flex items-center gap-3 h-10">
                    <button type="button" data-testid="edit-user-active-toggle"
                      onClick={() => setEditData({ ...editData, is_active: !editData.is_active })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editData.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editData.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <span className={`text-sm font-semibold ${editData.is_active ? 'text-emerald-700' : 'text-red-600'}`}>
                      {editData.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              <Button data-testid="submit-edit-user" type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Save Changes</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Reset Password</DialogTitle>
            <DialogDescription>Set a new password for this user.</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm font-semibold text-blue-900">{selectedUser.name}</p>
                <p className="text-xs text-slate-600">{selectedUser.email}</p>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">New Password</Label>
                <Input data-testid="reset-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="mt-2 rounded border-blue-200" placeholder="Enter new password" />
              </div>
              <Button data-testid="submit-reset-password" type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Reset Password</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-red-700">Delete User</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="bg-red-50 p-4 rounded border border-red-200">
                <p className="text-sm font-semibold text-red-900">Are you sure you want to delete <strong>{selectedUser.name}</strong>?</p>
                <p className="text-xs text-red-700 mt-1">{selectedUser.email} - {roleLabel(selectedUser.role)}</p>
              </div>
              <div className="flex gap-3">
                <Button data-testid="cancel-delete-user" variant="outline" onClick={() => setDeleteOpen(false)} className="flex-1 rounded">Cancel</Button>
                <Button data-testid="confirm-delete-user" onClick={handleDelete} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded font-bold">Delete User</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Filter Tabs + Search */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="user-filter-tabs">
        <button onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${statusFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="filter-all">
          All ({users.length})
        </button>
        <button onClick={() => setStatusFilter('active')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center gap-1.5 ${statusFilter === 'active' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="filter-active">
          <UserCheck size={14} /> Active ({activeCount})
        </button>
        <button onClick={() => setStatusFilter('inactive')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center gap-1.5 ${statusFilter === 'inactive' ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="filter-inactive">
          <UserX size={14} /> Inactive ({inactiveCount})
        </button>
        <div className="relative flex-1 min-w-[220px] max-w-md ml-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="users-search-input" placeholder="Search users by name, email, role, team…" value={searchText} onChange={e => setSearchText(e.target.value)} className="pl-9 rounded border-blue-200" />
        </div>
      </div>

      <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50 border-b border-blue-100">
              <TableHead className="font-bold text-blue-900">Emp #</TableHead>
              <TableHead className="font-bold text-blue-900">Name</TableHead>
              <TableHead className="font-bold text-blue-900">Email</TableHead>
              <TableHead className="font-bold text-blue-900">Role</TableHead>
              <TableHead className="font-bold text-blue-900">Status</TableHead>
              <TableHead className="font-bold text-blue-900">Designation</TableHead>
              <TableHead className="font-bold text-blue-900">Team</TableHead>
              <TableHead className="font-bold text-blue-900">DOJ</TableHead>
              <TableHead className="font-bold text-blue-900">Exit Date</TableHead>
              <TableHead className="font-bold text-blue-900">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-slate-500 py-8">
                  <UserCog size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>{statusFilter === 'inactive' ? 'No inactive users.' : statusFilter === 'active' ? 'No active users.' : 'No users found.'}</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className={`hover:bg-blue-50 ${u.is_active === false ? 'opacity-60 bg-slate-50' : ''}`}>
                  <TableCell className="text-sm font-mono text-slate-700" data-testid={`user-emp-num-${u.id}`}>{u.employee_number || <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell className="font-semibold text-blue-900">
                    <div className="flex items-center gap-2">
                      {u.name}
                      {u.is_active === false && <UserX size={14} className="text-red-400" />}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 text-xs font-bold uppercase rounded ${roleBadge(u.role)}`}>
                      {roleLabel(u.role)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span data-testid={`user-status-${u.id}`} className={`px-2 py-1 text-xs font-bold rounded ${u.is_active === false ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {u.is_active === false ? 'Inactive' : 'Active'}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{u.designation || <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell className="text-sm">{u.team_name || <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell className="text-sm text-slate-600" data-testid={`user-doj-${u.id}`}>{u.date_of_joining ? new Date(u.date_of_joining).toLocaleDateString() : <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell className="text-sm text-slate-500">{u.date_of_exit ? new Date(u.date_of_exit).toLocaleDateString() : <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" data-testid={`edit-user-${u.id}`} title="Edit User"
                        onClick={() => { setSelectedUser(u); setEditData({ name: u.name, role: u.role, team_id: u.team_id || '', employee_number: u.employee_number || '', designation: u.designation || '', department: u.department || '', date_of_birth: u.date_of_birth || '', date_of_joining: u.date_of_joining || '', marriage_anniversary: u.marriage_anniversary || '', date_of_exit: u.date_of_exit || '', is_active: u.is_active !== false }); setEditOpen(true); }}
                        className="text-blue-600 hover:text-blue-900 hover:bg-blue-50">
                        <Edit2 size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`reset-pwd-${u.id}`} title="Reset Password"
                        onClick={() => { setSelectedUser(u); setNewPassword(''); setResetOpen(true); }}
                        className="text-amber-600 hover:text-amber-900 hover:bg-amber-50">
                        <KeyRound size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`delete-user-${u.id}`} title="Delete User"
                        onClick={() => { setSelectedUser(u); setDeleteOpen(true); }}
                        className="text-red-600 hover:text-red-900 hover:bg-red-50">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
